import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Trophy, Crown, Lightning, Target, Shield, ChartLineUp,
  ShareNetwork, DeviceMobile, Copy, CheckCircle,
  WhatsappLogo, XLogo, FacebookLogo,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const APP_URL = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "https://pitchiq.win";
const PUBLIC_URL = "https://pitchiq.win";
const SHARE_TEXT = "Become the Ultimate Football Predictor — score points on every Premier League match at PitchIQ.";

const benefits = [
  { icon: Trophy,       title: "Forecast Every Fixture", body: "Every Premier League match, all 380 of them. Free to play, all season." },
  { icon: Crown,        title: "Climb the Cup",          body: "Top the global leaderboard or your private league to lift the Premier Predictors Cup." },
  { icon: Lightning,    title: "Skill-Based Scoring",    body: "Exact score = 10 + goals. Correct result = 5. Strategy and form-watching matter." },
  { icon: Shield,       title: "Premier Tier (£9.99)",   body: "Full season fixtures, private leagues with friends, deep stats dashboard, Premier badge." },
  { icon: ChartLineUp,  title: "Track Your Form",        body: "See your accuracy, exact-score streak and weekly points trend over the season." },
  { icon: Target,       title: "Tribal Pride",           body: "Pick your favourite club — fans go head-to-head on the Club Tribes leaderboard." },
];

export default function GetStarted() {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);

  const shareUrl = PUBLIC_URL;
  const encodedText = encodeURIComponent(SHARE_TEXT);
  const encodedUrl = encodeURIComponent(shareUrl);

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "PitchIQ", text: SHARE_TEXT, url: shareUrl });
      } catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied — paste it anywhere");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy — long-press the URL bar instead");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20" data-testid="get-started-page">
      {/* HERO */}
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <span className="text-xs uppercase tracking-[0.3em] text-primary-yellow font-bold">Season 2026/27 · Now Open</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase font-black mt-3 leading-[0.95]">
            Become the<br />
            <span className="text-primary-yellow">Ultimate Football</span><br />
            Predictor
          </h1>
          <p className="text-white/70 text-base sm:text-lg mt-6 max-w-xl leading-relaxed">
            Forecast every Premier League fixture. Compete with friends, fellow fans, and the world. Lift the Premier Predictors Cup.
          </p>

          {/* CTAs */}
          <div className="mt-8 flex flex-wrap gap-3" data-testid="get-started-ctas">
            <Link
              data-testid="cta-open-app"
              to={user ? "/fixtures" : "/register"}
              className="bg-primary-yellow text-black font-bold uppercase tracking-[0.2em] text-xs px-7 py-4 hover:-translate-y-0.5 transition-transform flex items-center gap-2"
            >
              {user ? "Open App" : "Get Started — Free"} <Lightning weight="fill" />
            </Link>
            <button
              data-testid="cta-install"
              onClick={() => toast.info("On mobile: tap your browser's 'Share' menu → 'Add to Home Screen'", { duration: 6000 })}
              className="border border-white/20 hover:border-primary-yellow text-white hover:text-primary-yellow font-bold uppercase tracking-[0.2em] text-xs px-7 py-4 flex items-center gap-2 transition-colors"
            >
              <DeviceMobile weight="bold" /> Install on Phone
            </button>
          </div>

          {/* Trust strip */}
          <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-[10px] uppercase tracking-[0.25em] text-white/40">
            <span>· Stripe Secured</span>
            <span>· Bcrypt Passwords</span>
            <span>· UK-Based</span>
            <span>· No Ads (Yet)</span>
          </div>
        </div>

        {/* Cup graphic */}
        <div className="lg:col-span-5 order-1 lg:order-2">
          <div className="relative aspect-square max-w-md mx-auto" data-testid="cup-graphic">
            <div className="absolute inset-0 bg-primary-yellow/20 rounded-full blur-3xl scale-90" />
            <div className="relative w-full h-full surface border border-primary-yellow/30 flex items-center justify-center glow-yellow">
              <Trophy size={180} weight="fill" className="text-primary-yellow drop-shadow-[0_8px_32px_rgba(227,255,0,0.5)]" />
            </div>
            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-[#0B0D11] border border-primary-yellow/40 px-4 py-2">
              <span className="text-[10px] uppercase tracking-[0.3em] text-primary-yellow font-bold">2026/27 Champion</span>
            </div>
          </div>
        </div>
      </div>

      {/* BENEFITS */}
      <section className="mt-24" data-testid="benefits-section">
        <span className="text-xs uppercase tracking-[0.3em] text-primary-yellow font-bold">Why PitchIQ</span>
        <h2 className="font-heading text-3xl sm:text-4xl uppercase font-black mt-2">Built For The Football Faithful</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {benefits.map(({ icon: Icon, title, body }) => (
            <div key={title} className="p-6 surface hover:bg-white/[0.03] transition-colors">
              <Icon size={28} weight="duotone" className="text-primary-yellow" />
              <h3 className="font-heading text-lg uppercase font-black mt-4 leading-tight">{title}</h3>
              <p className="text-white/60 text-sm mt-2 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SHARE */}
      <section className="mt-24 surface border border-white/10 p-8 sm:p-12 text-center" data-testid="share-section">
        <ShareNetwork size={36} weight="duotone" className="text-primary-yellow mx-auto" />
        <h2 className="font-heading text-3xl sm:text-4xl uppercase font-black mt-4">Spread The Word</h2>
        <p className="text-white/60 max-w-xl mx-auto mt-3 text-sm">
          Know a football fan who&apos;d love this? Share PitchIQ with one tap.
        </p>

        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <button
            data-testid="share-native"
            onClick={nativeShare}
            className="bg-primary-yellow text-black font-bold uppercase tracking-[0.2em] text-xs px-6 py-3 hover:-translate-y-0.5 transition-transform flex items-center gap-2"
          >
            <ShareNetwork weight="bold" /> Share
          </button>

          <a
            data-testid="share-whatsapp"
            href={`https://wa.me/?text=${encodedText}%20${encodedUrl}`}
            target="_blank" rel="noopener noreferrer"
            className="border border-white/20 hover:border-[#25D366] text-white hover:text-[#25D366] font-bold uppercase tracking-[0.2em] text-xs px-6 py-3 flex items-center gap-2 transition-colors"
          >
            <WhatsappLogo weight="fill" /> WhatsApp
          </a>

          <a
            data-testid="share-x"
            href={`https://x.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`}
            target="_blank" rel="noopener noreferrer"
            className="border border-white/20 hover:border-white text-white font-bold uppercase tracking-[0.2em] text-xs px-6 py-3 flex items-center gap-2 transition-colors"
          >
            <XLogo weight="fill" /> X
          </a>

          <a
            data-testid="share-facebook"
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
            target="_blank" rel="noopener noreferrer"
            className="border border-white/20 hover:border-[#1877F2] text-white hover:text-[#1877F2] font-bold uppercase tracking-[0.2em] text-xs px-6 py-3 flex items-center gap-2 transition-colors"
          >
            <FacebookLogo weight="fill" /> Facebook
          </a>

          <button
            data-testid="share-copy"
            onClick={copyLink}
            className="border border-white/20 hover:border-primary-yellow text-white hover:text-primary-yellow font-bold uppercase tracking-[0.2em] text-xs px-6 py-3 flex items-center gap-2 transition-colors"
          >
            {copied ? <CheckCircle weight="fill" /> : <Copy weight="bold" />} {copied ? "Copied" : "Copy Link"}
          </button>
        </div>

        <div className="mt-8 inline-block bg-[#0B0D11] border border-white/10 px-4 py-2 font-mono-num text-xs text-white/60">
          {shareUrl}
        </div>
      </section>

      {/* Final CTA */}
      <section className="mt-24 text-center">
        <h2 className="font-heading text-3xl sm:text-4xl uppercase font-black">Ready To Predict?</h2>
        <p className="text-white/60 mt-3">Free to play. No credit card needed. Cup awaits.</p>
        <Link
          to={user ? "/fixtures" : "/register"}
          data-testid="cta-bottom"
          className="inline-block mt-6 bg-primary-yellow text-black font-bold uppercase tracking-[0.2em] text-sm px-10 py-5 hover:-translate-y-0.5 transition-transform"
        >
          {user ? "Make Your Picks" : "Create Free Account"} →
        </Link>
      </section>
    </div>
  );
}
